import { Model, ValidationTask } from '@lucidtech/las-sdk-browser';
import { FormApi, revalidateLogic, useForm } from '@tanstack/react-form';
import { memo, useCallback, useEffect, useMemo } from 'react';

import {
  FormContext,
  useAgentRunTree,
  useDocumentAnnotations,
  useGetAgentFromPath,
  useGetAgentRunFromPath,
  usePredictionAnnotations,
} from '@/hooks';
import {
  toDocumentId,
  toFileId,
  toModelId,
  toPredictionId,
  toValidationTaskId,
  useGetDocument,
  useGetFile,
  useGetModel,
  useGetPrediction,
  useGetValidationTask,
  useUpdateValidationTask,
} from '@/hooks/api';
import { DocumentProvider as DocumentProvider_, FormValues, useDocumentStore } from '@/store';
import {
  Annotations,
  DivProps,
  findModelId,
  findPredictionId,
  findValidationId,
  findValidationTaskId,
  toAnnotations,
  toFormValues,
} from '@/utils';

const isTaskNotCompleted = (task?: ValidationTask | null) => {
  return !!task && ['ready', 'running'].includes(task.status);
};

const useGetFormValues = (
  documentId?: string,
  predictionId?: string,
  model?: Model,
  validationId?: string,
  taskId?: string
) => {
  const setIsLoading = useDocumentStore((state) => state.setIsLoading);
  const {
    annotations: documentAnnotations,
    isPending: isPendingDocumentAnnotations,
    lastModified: lastModifiedDocumentAnnotations,
  } = useDocumentAnnotations({
    documentId,
  });
  const {
    annotations: predictionAnnotations,
    isPending: isPendingPredictionAnnotations,
    lastModified: lastModifiedPredictionAnnotations,
  } = usePredictionAnnotations({
    predictionId,
  });

  const { data: task, isPending: isPendingValidationTask } = useGetValidationTask(
    toValidationTaskId(validationId, taskId)
  );

  useEffect(() => {
    setIsLoading(isPendingDocumentAnnotations || isPendingPredictionAnnotations);
  }, [isPendingDocumentAnnotations, isPendingPredictionAnnotations, setIsLoading]);

  const defaultState = useMemo(() => {
    if (!model) return null;
    if (taskId && isPendingValidationTask) return null;
    if (isPendingDocumentAnnotations || isPendingPredictionAnnotations) return null;

    /* Sort annotations by lastModified */
    const allAnnotations: { annotations: Annotations; lastModified: Date }[] = [];

    if (documentAnnotations != null && lastModifiedDocumentAnnotations != null) {
      allAnnotations.push({ annotations: documentAnnotations, lastModified: lastModifiedDocumentAnnotations });
    }

    if (predictionAnnotations != null && lastModifiedPredictionAnnotations != null) {
      allAnnotations.push({ annotations: predictionAnnotations, lastModified: lastModifiedPredictionAnnotations });
    }

    allAnnotations.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());

    /* Avoid creating form with empty (null) default values */
    if (allAnnotations.length === 0) return null;

    const { values, meta } = toFormValues(model.fieldConfig, ...allAnnotations.map((a) => a.annotations));

    if (isTaskNotCompleted(task)) {
      return { values, fieldMetaBase: meta };
    } else {
      return { values };
    }
  }, [
    model,
    taskId,
    isPendingValidationTask,
    isPendingDocumentAnnotations,
    isPendingPredictionAnnotations,
    documentAnnotations,
    lastModifiedDocumentAnnotations,
    predictionAnnotations,
    lastModifiedPredictionAnnotations,
    task,
  ]);

  return defaultState;
};

type OnSubmit = { value: FormValues; formApi: FormApi<FormValues, undefined> };

const DocumentStateWrapper = ({ children }: DocumentProviderProps) => {
  const { data: agent } = useGetAgentFromPath();
  const { data: run } = useGetAgentRunFromPath();
  const { taskDocument } = useAgentRunTree({ agentId: run?.agentId, runId: run?.runId });

  const taskId = useMemo(() => findValidationTaskId(run), [run]);
  const modelId = useMemo(() => findModelId(agent), [agent]);
  const validationId = useMemo(() => findValidationId(agent), [agent]);
  const predictionId = useMemo(() => findPredictionId(run), [run]);
  const documentId = useMemo(() => taskDocument?.documentId, [taskDocument]);

  const setDocumentBlob = useDocumentStore((state) => state.setDocumentBlob);
  const setFieldConfig = useDocumentStore((state) => state.setFieldConfig);
  const setStatus = useDocumentStore((state) => state.setStatus);
  const setDocumentId = useDocumentStore((state) => state.setDocumentId);
  const setAnnotationFileUrl = useDocumentStore((state) => state.setAnnotationFileUrl);
  const setIsEditing = useDocumentStore((state) => state.setIsEditing);
  const setPredictedPages = useDocumentStore((state) => state.setPredictedPages);
  const setShowPages = useDocumentStore((state) => state.setShowPages);

  const { data: doc } = useGetDocument(toDocumentId(documentId));
  const { data: documentBlob } = useGetFile(toFileId(doc?.fileUrl));
  const { data: model } = useGetModel(toModelId(modelId));
  const { data: task } = useGetValidationTask(toValidationTaskId(validationId, taskId));
  const { data: prediction } = useGetPrediction(toPredictionId(predictionId));

  const { mutateAsync: updateTask } = useUpdateValidationTask();

  const defaultFormState = useGetFormValues(documentId, predictionId, model, validationId, taskId);

  const onSubmit = useCallback(
    ({ value }: OnSubmit) => {
      if (!model) return;
      const annotations = toAnnotations(model.fieldConfig, value);
      updateTask({
        id: toValidationTaskId(validationId, taskId),
        updates: { output: annotations, status: 'succeeded' },
      });
    },
    [model, taskId, updateTask, validationId]
  );

  const form = useForm({
    defaultState: defaultFormState,
    onSubmit,
    validationLogic: revalidateLogic(),
  });

  const predictedPages = useMemo(() => {
    if (!prediction) return;
    if (prediction?.preprocessConfig?.pages) return prediction.preprocessConfig.pages as number[];
    return Array.from({ length: prediction?.preprocessConfig?.maxPages ?? 0 }, (_, i) => i);
  }, [prediction]);

  useEffect(() => setFieldConfig(model?.fieldConfig), [model?.fieldConfig, setFieldConfig]);
  useEffect(() => setStatus(run?.status), [run?.status, setStatus]);
  useEffect(() => setDocumentBlob(documentBlob), [documentBlob, setDocumentBlob]);
  useEffect(() => setAnnotationFileUrl(doc?.annotationFileUrl), [doc?.annotationFileUrl, setAnnotationFileUrl]);
  useEffect(() => setDocumentId(doc?.documentId), [doc?.documentId, setDocumentId]);
  useEffect(() => setIsEditing(isTaskNotCompleted(task)), [task, setIsEditing]);
  useEffect(() => setPredictedPages(predictedPages), [predictedPages, setPredictedPages]);
  useEffect(() => setShowPages(undefined), [setShowPages]);

  return <FormContext.Provider value={form}>{children}</FormContext.Provider>;
};

export type DocumentProviderProps = DivProps & {
  documentId?: string;
  modelId?: string;
  predictionId?: string;
  agentId?: string;
  validationId?: string;
  taskId?: string;
  runId?: string;
};

export const IDocumentProvider = ({ children }: DocumentProviderProps) => {
  return (
    <DocumentProvider_>
      <DocumentStateWrapper>{children}</DocumentStateWrapper>
    </DocumentProvider_>
  );
};

export const DocumentProvider = memo(IDocumentProvider) as typeof IDocumentProvider;
